import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import optionsSlice from "redux/features/optionData";
import { useAuthenticationState } from "redux/features/auth";
import EnhancedSelect from "components/enhancedSelect";
import { Columns, Box, Message, Form, Button } from "react-bulma-components";
import { RadioColumns } from "util/optionColumns";

import { FaMinus, FaPlus, FaArrowUp, FaArrowDown } from "react-icons/fa";
import IconButton from "util/iconButton";
import WithToolTip from "util/tooltip";
import TrimmedInput from "common-frontend/util/trimmedInput";

import { PromptLocation } from "components/shared/location";
import { useGeographicalLocations } from "redux/features/countries";
import trimAccents from "util/trimAccents";

const { Field } = Form;
/*

SETUP OF INSTITUTION OPTIONS

Each option will be something that can be selected from the searchable
select box, and will have one of the following formats:

  (a) If an existing institution is selected:
  {
     value: the institution id
     label: the institution name
     aliases, asciiLabel, labelExtras: used by our enhancedSelect widget
     fullItem: {    (the actual institution information from the server)
         id, name, nameIsVerified, otherNames, otherNameStatus,
         countryName, areaName, localityName,
         later: countryId, areaId
     }
  }

  (b) If "register a new institution" is selected:
  {
     __isNew__: true,
     value: the new institution name
     label: the new institution name
  }

When nothing is selected, the "selected option" is null.

 */

function institutionLocationName(institution) {
  return (
    [institution.localityName, institution.areaName, institution.countryName]
      .filter((name) => !!name)
      .join(", ") || null
  );
}

const institutionLoadConfig = {
  url: "/institutions",
  reportReloadFailures: true,
  optionConfig: {
    labelField: "name",
    valueField: "id",
    aliases: [["otherNames", "alternative name"]],
    labelExtras: [(item) => institutionLocationName(item)],
  },
};

export function useInstitutionOptions() {
  const { selector } = optionsSlice("institutions");
  return useSelector(selector);
}

export function useInstitutionLookup() {
  const options = useInstitutionOptions();
  return Object.fromEntries(
    Object.entries(options.index).map(([id, option]) => [id, option.fullItem])
  );
}

export function useInstitutionOptionsReload(reloadNeeded) {
  const {
    asyncActions: { reload },
  } = optionsSlice("institutions");
  const dispatch = useDispatch();

  useEffect(() => {
    if (reloadNeeded) dispatch(reload());
  });
}

export function usePreloadInstitutionOptions() {
  const {
    selector,
    asyncActions: { loader, reload },
  } = optionsSlice("institutions");
  const institutionOptions = useSelector(selector);
  const dispatch = useDispatch();
  const uninitialized =
    institutionOptions.loaded === null || institutionOptions.failed;
  useEffect(() => {
    if (uninitialized) {
      dispatch(loader(institutionLoadConfig));
    }
    const timerId = window.setInterval(() => dispatch(reload()), 60000);
    return () => window.clearInterval(timerId);
  }, [uninitialized, dispatch, loader, reload]);

  return institutionOptions;
}

/*

INSTITUTION SELECTION COMPONENT

SelectInstitution is the component for allowing the user to select an
institution for one of their affiliations. Props are:

  * institutionOptions:
      institution option state and options list, as above

  * excludeInstitutions:
      object with keys for every institution id already used in this set of
      affiliations, and hence should not be offered in the list

  * associatedInstitutions:
      object with keys for every institution id in a current or past
      affiliation for this person, meaning the institution will always
      show up in the list, whereas institutions NOT entered here and
      that belong to less than a small threshold number of affiliaitions
      are assumed to be typos for a "real" institution name and will be
      omitted from the list unless they are the only match or are an exact
      match. This is implemented by declaring them "unlikely" when calling
      EnhancedSelect. If associatedInstitutions is null or undefined, nothing
      will be omitted and all institutions will be shown.

  * affiliationRecord:
      current state of the proposed affiliation record, in a form suitable for
      sending to the API endpoint. If an institution has not yet been selected
      this will be an empty object, otherwise it will have entries

           institution_id         ( id of institution, or 'new')
           new_institution_name   ( name of new institution to create
                                    if institution_id is 'new', else undefined )
           department_name        ( see documentation elsewhere )
           location
           institution_location

  * updateAffiliationRecord:
      callback to update the affiliation record with new values. (Values that
      are present but undefined in new values will be deleted from the record).

  * clearingIsDeleting:
      boolean indicating that the institution is optional and the prompt is
      for either a single institution or the last institution in a mult-
      institution list. When this flag is set, it means that clearing
      the institution (without selecting a new one in its place) will
      persist after blurring and will result in a removal of the affiliation
      from the list of proposed changed affiliations. This is indicated
      to the user by having the clear indicator be the original react-select
      cross (signifying deletion) rather than our customized "Change" button.
*/

// Institutions with fewer affiliations than this will be suppressed unless
const UNLIKELY_INSTITUTION_THRESHOLD = 2;
export function SelectInstitution({
  institutionOptions,
  excludeInstitutions,
  associatedInstitutions,
  affiliationRecord,
  updateAffiliationRecord,
  clearingIsDeleting,
}) {
  const selectedOption =
    affiliationRecord.institution_id === "new"
      ? {
          __isNew__: true,
          value: affiliationRecord.new_institution_name,
          label: affiliationRecord.new_institution_name,
        }
      : affiliationRecord.institution_id === undefined
      ? null
      : institutionOptions.index[affiliationRecord.institution_id];

  const [previouslySelectedOption, setPreviouslySelectedOption] = useState(
    selectedOption
  );

  useEffect(() => {
    if (clearingIsDeleting && affiliationRecord.institution_id === undefined)
      setPreviouslySelectedOption(null);
  }, [affiliationRecord.institution_id, clearingIsDeleting]);

  const setSelection = useCallback(
    (selection) => {
      const institution_id = !selection
        ? undefined
        : selection.__isNew__
        ? "new"
        : selection.value;

      const new_institution_name =
        institution_id === "new" ? selection.value : undefined;
      if (selection && !selection.__isNew__)
        setPreviouslySelectedOption(selection);
      return updateAffiliationRecord({
        institution_id,
        new_institution_name,
      });
    },
    [setPreviouslySelectedOption, updateAffiliationRecord]
  );

  const onIncompleteSelection = useCallback(
    (text) => {
      if (text === "") setSelection(previouslySelectedOption);
    },
    [previouslySelectedOption, setSelection]
  );

  const checkOptionUnlikely = associatedInstitutions
    ? (o) =>
        o.fullItem.numAffiliations < UNLIKELY_INSTITUTION_THRESHOLD &&
        !associatedInstitutions[o.value]
    : null;

  return (
    <EnhancedSelect
      datasetName="institutions"
      excludeOptions={excludeInstitutions}
      checkOptionUnlikely={checkOptionUnlikely}
      loadConfig={institutionLoadConfig}
      minCharacters={1}
      placeholder={"Institution name (type part of the name to search)"}
      showSelectionAsIs
      createNewWording={(labelSpan) => (
        <Fragment>
          register a new institution whose full name is {labelSpan}
        </Fragment>
      )}
      selection={selectedOption}
      setSelection={setSelection}
      onIncompleteSelection={onIncompleteSelection}
      keepCrossAsClearIndicator={clearingIsDeleting}
    />
  );
}

/*

AFFILIATION LOCATION SELECTION AND CONFLICT RESOLUTION

*/

function ResolveLocationConflict({
  institution,
  institutionOptions,
  affiliationDetails,
  updateAffiliationRecord,
  setHomeLocation,
}) {
  const { countries } = useGeographicalLocations();
  const { authenticatedAsStaff: amStaff } = useAuthenticationState();
  const Iam = amStaff ? "this person is" : "I am";

  const { locationDetails, institutionLocationDetails } = affiliationDetails;
  const {
    approved_institution_location,
    institution_location_change_rejection,
  } = affiliationDetails.original;

  const [locationCountry] = countries.filter(
    (c) => c.id === locationDetails.properties.country_id
  );

  let multiNationalOption = null;
  if (affiliationDetails.conflictingCountry) {
    const suggestSubsidiaryName =
      institution.name + " " + locationCountry.label;
    const trimmedSuggestion = trimAccents(suggestSubsidiaryName);

    const matches = institutionOptions.options.filter(
      (o) => o.asciiLabel === trimmedSuggestion
    );
    const [subsidiary] =
      matches.length > 0
        ? matches
        : institutionOptions.options.filter((o) =>
            o.aliases.some(
              ([description, display, ascii]) => ascii === trimmedSuggestion
            )
          );
    multiNationalOption = subsidiary
      ? {
          id: subsidiary.value,
          name: subsidiary.label,
        }
      : {
          name: suggestSubsidiaryName,
        };
  }

  const [resolution, setResolution] = useState(null);
  const resolve = () => {
    if (resolution === "change-institution-location")
      updateAffiliationRecord({
        institution_location: affiliationDetails.location,
      });
    else if (resolution === "keep-institution-location")
      updateAffiliationRecord({
        location: affiliationDetails.institution_location,
      });
    else if (resolution === "set-remote") {
      setHomeLocation(affiliationDetails.location);
      updateAffiliationRecord({
        location: affiliationDetails.institution_location,
      });
    } else if (resolution.startsWith("switch-to-subsidiary-id-")) {
      const id = resolution.replace("switch-to-subsidiary-id-", "");
      updateAffiliationRecord({ institution_id: id });
    } else if (resolution.startsWith("switch-to-subsidiary-new-named-")) {
      const name = resolution.replace("switch-to-subsidiary-new-named-", "");
      updateAffiliationRecord({
        institution_id: "new",
        new_institution_name: name,
      });
    }
  };
  return (
    <Message color="danger">
      <Message.Header>Conflicting location information</Message.Header>
      <Message.Body>
        You've specified <b> {locationDetails.properties.full_label}</b> as the
        location for {institution.name}, but
        {approved_institution_location
          ? " you have previously asked our staff to set the location to "
          : " in our database the location is "}
        <b>{institutionLocationDetails.properties.full_label}</b>
        {institution_location_change_rejection
          ? " (the request you made to have the location changed by our " +
            "staff was not approved; our staff said: " +
            institution_location_change_rejection.response +
            ")"
          : null}
        . Which is correct?
        <Field>
          <RadioColumns
            name={"resolve-institution-location-conflict-" + institution.id}
            value={resolution}
            optionValue="keep-institution-location"
            onChange={setResolution}
          >
            <b>{institutionLocationDetails.properties.full_label}</b> is
            correct.
          </RadioColumns>
        </Field>
        <Field>
          <RadioColumns
            name={"resolve-institution-location-conflict-" + institution.id}
            value={resolution}
            optionValue="set-remote"
            onChange={setResolution}
          >
            <b>{institutionLocationDetails.properties.full_label}</b> is the
            correct location for {institution.name}, but {Iam + " "}
            affiliated remotely from{" "}
            {locationDetails.properties.full_label + " "}
            (for example, away on leave, or working/studying remotely for other
            reasons).
          </RadioColumns>
        </Field>
        {multiNationalOption ? (
          <Field>
            <RadioColumns
              name={"resolve-institution-location-conflict-" + institution.id}
              value={resolution}
              optionValue={
                "switch-to-subsidiary-" + multiNationalOption.id === undefined
                  ? "new-named-" + multiNationalOption.name
                  : "id-" + multiNationalOption.id
              }
              onChange={setResolution}
            >
              <b>{institutionLocationDetails.properties.full_label}</b> is where
              {" " + institution.name} is based, but it also has a subsidiary in
              {" " + locationCountry.label}, which is where {Iam}.{" "}
              <i>
                We consider this a separate institution. When you press
                Continue, your affiliation will switched to
                {multiNationalOption.id === undefined
                  ? " a new institution named "
                  : null}
                {multiNationalOption.name}.
                {multiNationalOption.id === undefined
                  ? " Please adjust the name appropriately if needed."
                  : null}
              </i>
            </RadioColumns>
          </Field>
        ) : null}
        <Field>
          <RadioColumns
            name={"resolve-institution-location-conflict-" + institution.id}
            value={resolution}
            optionValue="change-institution-location"
            onChange={setResolution}
          >
            <b>{locationDetails.properties.full_label}</b> is correct;{" "}
            {institutionLocationDetails.properties.full_label} is wrong.{" "}
            <i>
              Selecting this option will
              {approved_institution_location ===
              locationDetails.properties.symbolic_id
                ? " cancel your previous location change request."
                : (amStaff
                    ? ""
                    : institution_location_change_rejection
                    ? " submit another request to our staff (please " +
                      "consult with them first to explain, however) to "
                    : " submit a request to our staff to") +
                  "   change the institution's location in our database."}
            </i>
          </RadioColumns>
        </Field>
        <div className="buttons mt-4">
          <Button disabled={!resolution} onClick={resolve}>
            Continue
          </Button>
        </div>
      </Message.Body>
    </Message>
  );
}

function PromptNationwideInstitution({
  // Pass id/name separately, because this may be an institution that
  // has not yet been created, so there may not be an actual institution
  // record yet (in which case we will use the "new-New Name Here"
  // psuedo-id that is used in affiliationDetails).
  id,
  name,
  selection,
  locationDetails,
  updateAffiliationRecord,
}) {
  if (!locationDetails.properties) return null;

  return (
    <Fragment>
      <Field>
        <RadioColumns
          name={"nationwide-" + id}
          value={selection}
          optionValue={locationDetails.properties.symbolic_id}
          onChange={(v) => updateAffiliationRecord({ institution_location: v })}
        >
          <b>{name}</b> is located only (or mostly) in{" "}
          {locationDetails.properties.label}
        </RadioColumns>
      </Field>
      <Field>
        <RadioColumns
          name={"nationwide-" + id}
          value={selection}
          optionValue={locationDetails.properties.country_symbolic_id}
          onChange={(v) => updateAffiliationRecord({ institution_location: v })}
        >
          It is nationwide.
        </RadioColumns>
      </Field>
    </Fragment>
  );
}

function EditAffiliationLocation({
  affiliationRecord,
  updateAffiliationRecord,
  affiliationDetails,
  institutionOptions,
  setHomeLocation,
}) {
  if (affiliationRecord.institution_id === undefined) return null;
  const {
    institution,
    id,
    name,
    isNew,
    allowNationwideInstitution,
  } = affiliationDetails;

  const isConflict =
    affiliationDetails.conflictingCountry || affiliationDetails.conflictingArea;

  const {
    institutionLocationDetails,
    approvedInstitutionLocationDetails,
    institution_location_change_rejection,
    rejectedInstitutionLocationDetails,
  } = affiliationDetails.original;

  const submittedChangeRequest =
    approvedInstitutionLocationDetails &&
    approvedInstitutionLocationDetails.properties
      ? {
          from: approvedInstitutionLocationDetails.properties.full_label,
          to: institutionLocationDetails.properties.full_label,
        }
      : null;

  if (isConflict)
    return (
      <ResolveLocationConflict
        {...{
          institution,
          institutionOptions,
          affiliationDetails,
          updateAffiliationRecord,
          setHomeLocation,
        }}
      />
    );
  else
    return (
      <Fragment>
        <PromptLocation
          location={affiliationDetails.location}
          setLocation={(loc) =>
            updateAffiliationRecord(
              institutionLocationDetails.properties
                ? { location: loc }
                : { location: loc, institution_location: loc }
            )
          }
        ></PromptLocation>

        {affiliationDetails.changingInstitutionLocation ? (
          <p className="block submitting-request">
            {affiliationDetails.changingInstitutionLocation}
          </p>
        ) : submittedChangeRequest ? (
          <p className="block submitted-request">
            You have requested that our staff change this institution's location
            in our database, from <b>{submittedChangeRequest.from}</b> to{" "}
            <b>{submittedChangeRequest.to}</b>. This has not yet been done but
            will be looked at soon.
          </p>
        ) : institution_location_change_rejection ? (
          <p className="block rejected-request">
            You had requested that our staff change this institution's location
            in our database, from{" "}
            <b>{institutionLocationDetails.properties.full_label}</b> to{" "}
            <b>{rejectedInstitutionLocationDetails.properties.full_label}</b>.
            This request was not approved, for the following reason:{" "}
            {institution_location_change_rejection.response}.
          </p>
        ) : allowNationwideInstitution ? (
          <PromptNationwideInstitution
            id={isNew ? id : institution.id}
            name={isNew ? name : institution.name}
            selection={affiliationDetails.institution_location}
            updateAffiliationRecord={updateAffiliationRecord}
            locationDetails={affiliationDetails.locationDetails}
          />
        ) : null}
      </Fragment>
    );
}

/*

DEPARTMENT SELECTION

*/

function EditAffiliationDepartment({
  affiliationRecord,
  updateAffiliationRecord,
  affiliationDetails,
  institutionOptions,
}) {
  if (affiliationRecord.institution_id === undefined) return null;
  const institution = affiliationDetails.institution;
  const currentDepartment = affiliationDetails.department_name;
  const departmentNames = institution
    ? institution.departments.map((d) => d.name)
    : [];

  return (
    <div className="mt-2 pl-4 pr-4">
      <p>
        <i>(optional)</i> department (or division or campus etc), if applicable:
      </p>
      <div>
        {" "}
        <TrimmedInput
          value={currentDepartment ?? ""}
          setValue={(v) =>
            updateAffiliationRecord({
              department_name: v === "" ? null : v,
            })
          }
          autosuggest={
            departmentNames.length > 0
              ? {
                  suggestions: departmentNames,
                  id: "depts-for-" + affiliationRecord.institution_id,
                }
              : null
          }
        />
      </div>
    </div>
  );
}
/*

MAIN PROMPT FOR AFFILIATION-RELATED INFORMATION

Props are as follows.

 * statusDetails:
     The current proposed status details record. This is needed, rather than
     just the details of the affiliation currently being edited, for the
     implementation of actions such as swapping this affiliation with an
     adjacent one or adding a new affiliation.

 * index:
     This is one of the following:
     - 0-based index of which affiliation record is being edited, in the
       (non-empty) current "affiliations" list in statusDetails.
     - 0 when optional is set and the current "affiliations" list is empty,
       meaning to give the option of selecting an institution and add a new
       record to the affiliations list if (but only if) one is selcted.
     - null, meaning to generate an "add additional institution" prompt
       if the last entry in the current "affiliations" list has been
       filled in already.

 * setAffiliations:
     Callback to set a new value for the entire "affiliations" list in the
     change record. This is used when performing actions on the list as a
     whole, such as swapping or adding a new affiliation.

 * updateSingleAffiliationRecord:
     Callback to make a change to one specificaffiliation record, specified
     by position. When index is non-null, this component will create an
     updateThisAffiliationRecord callback to pass to its subcomponents
     by using index as the position.

 * setHomeLocation
     Callback to change the "home" location (independent of institution),
     used as an option for resolving location/institution-location conflicts
     by moving the conflicting location to the home location field.

 * optional:
     Boolean flag, set when editing the affiliation associated to an occupation
     for which providing an institution is optional.

 * institutionOptions, associatedInstitutions:
     to pass on to SelectInstitution

(this AffiliationPrompt component will take care of creating the
affiliationRecord, affiliationDetails, updateAffiliationRecord and
clearingIsDeleting props to pass to the SelectInstitution,
EditAffiliationDepartment, and EditAffiliationLocation subcomponents).

*/

export function AffiliationPrompt({
  statusDetails,
  index = null,
  setAffiliations,
  updateSingleAffiliationRecord,
  setHomeLocation,
  optional = false,
  institutionOptions,
  associatedInstitutions,
}) {
  const affiliations = statusDetails.affiliations;
  const l = affiliations.length;
  const showAdd =
    index === null && l > 0 && affiliations[l - 1].institution_id !== undefined;

  // Show remove button if more than one affiliation on the list.  If only one
  // affiliation is on the list, there is no remove button, but the button for
  // clearing selection behaves like a remove button if the institution is
  // optional.

  const showRemove = index !== null && affiliations.length > 1;
  const onAddRemove = () =>
    setAffiliations(
      showAdd
        ? [...affiliations, {}]
        : affiliations.filter((a, position) => position !== index)
    );

  const clearingIsDeleting =
    optional &&
    (affiliations.length === 0 || index === affiliations.length - 1);

  // The following parameters are needed only when index is non-null (
  // when index is null, all we do is generate a button to "add a new
  // affiliation").

  // Note that it is possible for index to be non-null but point to a non-
  // existent location in the proposed affiliations list - specifically, when
  // handling an optional institution where we don't want to create an entry
  // in the proposed affiliations list until after an institution is selected:
  // index will be zero but the affiliations list will be empty. In this case,
  // we set affiliationRecord and affiliationDetails to empty objects. The
  // code that uses affiliationDetails (prompting for department and location)
  // will do so only when affiliationRecord has an institution id defined
  // inside it.

  const {
    affiliationRecord,
    affiliationDetails,
    updateAffiliationRecord,
    excludeInstitutions,
  } =
    index === null
      ? {}
      : {
          affiliationRecord: affiliations[index] || {},
          affiliationDetails: statusDetails.affiliationDetailsList[index] || {},
          updateAffiliationRecord: (vals) =>
            updateSingleAffiliationRecord(vals, index),
          excludeInstitutions: Object.fromEntries(
            affiliations
              .filter((a, i) => i !== index && a.institution_id !== undefined)
              .map((a) => [a.institution_id, true])
          ),
        };

  const onSwap = (up) => {
    const otherIndex = up ? index - 1 : index + 1;
    const otherRecord = affiliations[otherIndex];
    setAffiliations(
      affiliations.map((a, i) =>
        i === index ? otherRecord : i === otherIndex ? affiliationRecord : a
      )
    );
  };

  return (
    <Fragment>
      <Columns breakpoint="mobile" gap={1} vCentered>
        <Columns.Column
          narrow
          className={
            showAdd || showRemove ? "add-or-remove-institution" : "is-invisible"
          }
        >
          <WithToolTip
            tip={index === null ? undefined : "Remove this institution"}
          >
            <IconButton
              icon={index === null ? FaPlus : FaMinus}
              onClick={onAddRemove}
            />
          </WithToolTip>
        </Columns.Column>
        <Columns.Column>
          {index === null ? (
            showAdd ? (
              <div className="add-prompt" onClick={onAddRemove}>
                <div>Add another institution</div>
              </div>
            ) : (
              <div className="add-instructions">
                Affiliated with more than one other institution? Specify this
                one first then you will be able to add another.
              </div>
            )
          ) : (
            <Box>
              <Columns multiline={false}>
                <Columns.Column size={7}>
                  <SelectInstitution
                    {...{
                      institutionOptions,
                      associatedInstitutions,
                      excludeInstitutions,
                      affiliationRecord,
                      updateAffiliationRecord,
                      clearingIsDeleting,
                    }}
                  />
                  <EditAffiliationDepartment
                    {...{
                      affiliationRecord,
                      affiliationDetails,
                      updateAffiliationRecord,
                      institutionOptions,
                    }}
                  />
                </Columns.Column>
                <Columns.Column size={5}>
                  <EditAffiliationLocation
                    {...{
                      affiliationRecord,
                      affiliationDetails,
                      updateAffiliationRecord,
                      setHomeLocation,
                      institutionOptions,
                    }}
                  />
                </Columns.Column>
              </Columns>
            </Box>
          )}
        </Columns.Column>
        <Columns.Column narrow gap={1}>
          {index !== null && index > 0 ? (
            <div>
              <WithToolTip tip={"Move up"}>
                <IconButton icon={FaArrowUp} onClick={() => onSwap(true)} />
              </WithToolTip>
            </div>
          ) : null}

          {index !== null && index < affiliations.length - 1 ? (
            <div>
              <WithToolTip tip={"Move down"}>
                <IconButton icon={FaArrowDown} onClick={() => onSwap(false)} />
              </WithToolTip>
            </div>
          ) : null}
        </Columns.Column>
      </Columns>
    </Fragment>
  );
}
